/**
 * useTimeMotionState - Event-driven state machine for time motion navigation
 *
 * Architecture mirrors usePlanetDragState:
 * - time.motion.open (input) → time.motion.start (for UI) + loadBuffer
 *
 * Listens to events:
 * - time.motion.open → starts loading buffer (from TimeMotionSettings Open button)
 * - time.motion.navigate → moves by index offset
 * - time.motion.confirm → applies selected time
 * - time.motion.cancel → cancels navigation
 * - time.motion.settings.changed → reloads with new step (if overlay open)
 *
 * Emits events:
 * - time.motion.start → when loading begins (for TimeMotionOverlay)
 * - time.motion.progress → loading progress updates
 * - time.motion.ready → buffer loaded, ready for navigation
 * - time.motion.move → planet positions updated
 * - time.motion.navigation.update → time/index changed
 */

import { devLog, devError, devWarn } from '../utils/devLog';
import { useCallback, useRef, useEffect } from 'react';
import { motionBufferService } from '../services/MotionBufferService';
import { globalEventStream, createEvent } from '../components/common/AstrologyChart/universal_architecture/core/EventStream';
import type { BaseEvent, EventSubscription } from '../components/common/AstrologyChart/universal_architecture/types/EventTypes';
import type { TimeMotionBuffer, TimeStepUnit } from '../types/MotionTypes';

const DEBUG = true;

// Default range for time motion buffer (300 steps each direction = 601 points)
const DEFAULT_RANGE = 300;

/**
 * Format Date with timezone offset for consistent timestamp comparison
 * Date stores local time values using UTC methods (to avoid browser timezone interference)
 */
function formatDateWithTimezone(date: Date, timezoneOffset: number): string {
  const offsetSign = timezoneOffset >= 0 ? '+' : '-';
  const offsetHours = Math.floor(Math.abs(timezoneOffset));
  const offsetMinutes = Math.round((Math.abs(timezoneOffset) - offsetHours) * 60);
  const offsetStr = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetStr}`;
}

interface UseTimeMotionStateProps {
  chartId: string;
  // Chart data for loading buffer
  latitude: number;
  longitude: number;
  timezoneOffset: number;
  currentTime: Date;
}

/**
 * useTimeMotionState hook
 */
export function useTimeMotionState({
  chartId,
  latitude,
  longitude,
  timezoneOffset,
  currentTime,
}: UseTimeMotionStateProps) {
  // Refs for current state
  const bufferRef = useRef<TimeMotionBuffer | null>(null);
  const currentIndexRef = useRef<number>(DEFAULT_RANGE); // Start at center
  const abortControllerRef = useRef<AbortController | null>(null);
  const isLoadingRef = useRef(false);
  const originalChartTimeRef = useRef<Date | null>(null); // Original chart time (preserved across step changes)

  // Refs for props (to avoid callback recreation on every render)
  const propsRef = useRef({ latitude, longitude, timezoneOffset, currentTime });
  propsRef.current = { latitude, longitude, timezoneOffset, currentTime };

  // ===========================================================================
  // Load Buffer
  // ===========================================================================

  const loadBuffer = useCallback(async (
    stepUnit: TimeStepUnit,
    stepValue: number,
    centerTime?: Date
  ) => {
    if (isLoadingRef.current) {
      if (DEBUG) devLog(`[TimeMotion:State] Already loading, skipping`);
      return;
    }

    // Abort previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    isLoadingRef.current = true;

    const props = propsRef.current;
    const effectiveCenterTime = centerTime ?? props.currentTime;

    if (DEBUG) devLog(`[TimeMotion:State] Loading buffer: ${stepValue} ${stepUnit}`);

    try {
      const buffer = await motionBufferService.loadTimeBuffer(
        effectiveCenterTime,
        props.latitude,
        props.longitude,
        props.timezoneOffset,
        stepUnit,
        stepValue,
        DEFAULT_RANGE,
        // onProgress
        (progress) => {
          globalEventStream.emit(createEvent<BaseEvent>(
            'time.motion.progress',
            'useTimeMotionState',
            {
              metadata: {
                chartId,
                progress,
              },
            }
          ));
        },
        // onInitialData
        (initialBuffer) => {
          if (DEBUG) devLog(`[TimeMotion:State] Initial data: ${initialBuffer.data.length} points`);
          bufferRef.current = initialBuffer;
          // Use universal method to find actual center (handles progressive loading)
          currentIndexRef.current = motionBufferService.findActualCenterIndex(initialBuffer);

          // Emit ready event with initial data
          const centerPoint = motionBufferService.getCenterPoint(initialBuffer);
          if (centerPoint) {
            emitReadyEvent(initialBuffer, centerPoint.t, true, centerPoint.o);
          }
        },
        // abortSignal
        abortControllerRef.current.signal,
        // onBufferUpdate
        (updatedBuffer) => {
          const oldCenterIndex = bufferRef.current?.centerIndex ?? DEFAULT_RANGE;
          bufferRef.current = updatedBuffer;

          // If user is at center, update currentIndexRef to new center
          // This handles centerIndex shift during progressive loading
          if (currentIndexRef.current === oldCenterIndex) {
            currentIndexRef.current = updatedBuffer.centerIndex;
            if (DEBUG) devLog(`[TimeMotion:State] Updated currentIndex to new center: ${updatedBuffer.centerIndex}`);
          }

          // Update navigation bounds
          emitNavigationUpdate(updatedBuffer, currentIndexRef.current);
        },
        // forceRefresh
        false
      );

      if (!buffer) {
        devError(`[TimeMotion:State] Failed to load buffer`);
        isLoadingRef.current = false;
        return;
      }

      bufferRef.current = buffer;
      // Use universal method for consistency
      currentIndexRef.current = motionBufferService.findActualCenterIndex(buffer);

      // Emit ready event with complete data
      const centerPoint = motionBufferService.getCenterPoint(buffer);
      if (centerPoint) {
        emitReadyEvent(buffer, centerPoint.t, false, centerPoint.o);
      }

      if (DEBUG) devLog(`[TimeMotion:State] Buffer loaded: ${buffer.data.length} points`);

    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        if (DEBUG) devLog(`[TimeMotion:State] Load aborted`);
      } else {
        devError(`[TimeMotion:State] Load error:`, error);
      }
    } finally {
      isLoadingRef.current = false;
    }
  }, [chartId]); // Props accessed via propsRef to avoid callback recreation

  // ===========================================================================
  // Emit Events
  // ===========================================================================

  const emitReadyEvent = useCallback((
    buffer: TimeMotionBuffer,
    timestamp: string,
    isPartial: boolean,
    apiUtcOffset: number = 0
  ) => {
    const centerIndex = buffer.centerIndex;
    const canGoBack = currentIndexRef.current > 0;
    const canGoForward = currentIndexRef.current < buffer.data.length - 1;

    globalEventStream.emit(createEvent<BaseEvent>(
      'time.motion.ready',
      'useTimeMotionState',
      {
        metadata: {
          chartId,
          stepUnit: buffer.stepUnit,
          stepValue: buffer.stepValue,
          timestamp,
          currentIndex: currentIndexRef.current,
          centerIndex,
          canGoBack,
          canGoForward,
          isPartial,
          progress: buffer.progress,
          chartTimezoneOffset: propsRef.current.timezoneOffset,
          apiUtcOffset,
        },
      }
    ));
  }, [chartId]);

  const emitNavigationUpdate = useCallback((
    buffer: TimeMotionBuffer,
    newIndex: number
  ) => {
    const point = buffer.data[newIndex];
    if (!point) {
      devWarn(`[TimeMotion:State] No point at index ${newIndex}`);
      return;
    }

    const canGoBack = newIndex > 0;
    const canGoForward = newIndex < buffer.data.length - 1;

    globalEventStream.emit(createEvent<BaseEvent>(
      'time.motion.navigation.update',
      'useTimeMotionState',
      {
        metadata: {
          chartId,
          newIndex,
          timestamp: point.t,
          canGoBack,
          canGoForward,
          isPartial: !buffer.isComplete,
          apiUtcOffset: point.o,
        },
      }
    ));
  }, [chartId]);

  // ===========================================================================
  // Navigation
  // ===========================================================================

  const handleNavigate = useCallback((event: BaseEvent) => {
    if (event.metadata?.chartId !== chartId) return;

    const buffer = bufferRef.current;
    if (!buffer) {
      devWarn(`[TimeMotion:State] No buffer for navigation`);
      return;
    }

    const indexDelta = event.metadata.indexDelta as number;
    let newIndex = currentIndexRef.current + indexDelta;

    // Clamp to buffer bounds
    newIndex = Math.max(0, Math.min(newIndex, buffer.data.length - 1));

    if (newIndex === currentIndexRef.current) {
      if (DEBUG) devLog(`[TimeMotion:State] Already at boundary`);
      return;
    }

    currentIndexRef.current = newIndex;

    if (DEBUG) devLog(`[TimeMotion:State] Navigate: index ${newIndex}, offset ${buffer.data[newIndex]?.i}`);

    // Get all planets at new index and emit move event
    const allPlanets = motionBufferService.getAllPlanetsAtTimeIndex(buffer, newIndex);

    globalEventStream.emit(createEvent<BaseEvent>(
      'time.motion.move',
      'useTimeMotionState',
      {
        metadata: {
          chartId,
          allPlanets,
        },
      }
    ));

    emitNavigationUpdate(buffer, newIndex);
  }, [chartId, emitNavigationUpdate]);

  // ===========================================================================
  // Event Handlers
  // ===========================================================================

  // Handle open event (from TimeMotionSettings Open button)
  const handleOpen = useCallback((event: BaseEvent) => {
    if (event.metadata?.chartId !== chartId) return;

    const stepUnit = event.metadata.stepUnit as TimeStepUnit;
    const stepValue = event.metadata.stepValue as number;

    if (DEBUG) devLog(`[TimeMotion:State] Open request: ${stepValue} ${stepUnit}`);

    // Save original chart time (only on fresh open, not when already navigating)
    originalChartTimeRef.current = propsRef.current.currentTime;

    // Emit start event for UI (like planet.drag.start)
    globalEventStream.emit(createEvent<BaseEvent>(
      'time.motion.start',
      'useTimeMotionState',
      {
        metadata: {
          chartId,
          stepUnit,
          stepValue,
          originalChartTime: formatDateWithTimezone(propsRef.current.currentTime, propsRef.current.timezoneOffset),
        },
      }
    ));

    loadBuffer(stepUnit, stepValue);
  }, [chartId, loadBuffer]);

  const handleConfirm = useCallback((event: BaseEvent) => {
    if (event.metadata?.chartId !== chartId) return;

    const buffer = bufferRef.current;
    if (!buffer) return;

    const selectedTime = event.metadata.selectedTime as string;

    if (DEBUG) devLog(`[TimeMotion:State] Confirm: ${selectedTime}`);

    // Emit confirmed event for chart recalculation (same pattern as planet.drag.confirmed)
    globalEventStream.emit(createEvent<BaseEvent>(
      'time.motion.confirmed',
      'useTimeMotionState',
      {
        metadata: {
          chartId,
          selectedTime,
        },
      }
    ));

    // Clean up
    bufferRef.current = null;
    currentIndexRef.current = DEFAULT_RANGE;
    originalChartTimeRef.current = null;
  }, [chartId]);

  const handleCancel = useCallback((event: BaseEvent) => {
    if (event.metadata?.chartId !== chartId) return;

    if (DEBUG) devLog(`[TimeMotion:State] Cancel`);

    // Abort any in-progress load
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Restore original planet positions before resetting
    const buffer = bufferRef.current;
    const originalPositions = buffer ? motionBufferService.getAllPlanetsAtCenter(buffer) : null;
    if (originalPositions) {
      // Emit move event to restore original positions
      globalEventStream.emit(createEvent<BaseEvent>(
        'time.motion.move',
        'useTimeMotionState',
        {
          metadata: {
            chartId,
            allPlanets: originalPositions,
          },
        }
      ));
    }

    // Clean up
    bufferRef.current = null;
    currentIndexRef.current = DEFAULT_RANGE;
    isLoadingRef.current = false;
    originalChartTimeRef.current = null;
  }, [chartId]);

  // Handle settings changed - reload buffer with new step, keeping current time as center
  // Only reloads if overlay is already open (buffer exists)
  const handleSettingsChanged = useCallback((event: BaseEvent) => {
    if (event.metadata?.chartId !== chartId) return;

    // If no buffer, overlay is not open - settings are already saved in TimeMotionSettings
    // Just ignore, will use new settings when Open is clicked
    const buffer = bufferRef.current;
    if (!buffer) {
      if (DEBUG) devLog(`[TimeMotion:State] Settings changed but overlay not open, ignoring`);
      return;
    }

    const stepUnit = event.metadata.stepUnit as TimeStepUnit;
    const stepValue = event.metadata.stepValue as number;

    if (!stepUnit || !stepValue) return;

    // Get current time from buffer (if navigated) to use as new center
    let centerTime = propsRef.current.currentTime;

    if (currentIndexRef.current !== undefined) {
      const currentPoint = buffer.data[currentIndexRef.current];
      if (currentPoint) {
        // Parse the timestamp from current position
        // API returns "2025-12-31T20:01:51+02:00" which JS parses as UTC (18:01:51 UTC)
        // We need to convert back to "local time stored as UTC" format for formatDateWithTimezone
        const parsed = new Date(currentPoint.t);
        const offsetMs = propsRef.current.timezoneOffset * 60 * 60 * 1000;
        centerTime = new Date(parsed.getTime() + offsetMs);
        if (DEBUG) devLog(`[TimeMotion:State] Settings changed: using current position as new center: ${currentPoint.t}`);
      }
    }

    if (DEBUG) devLog(`[TimeMotion:State] Settings changed: ${stepValue} ${stepUnit}`);

    // Reset loading state and load with new settings
    isLoadingRef.current = false;
    loadBuffer(stepUnit, stepValue, centerTime);
  }, [chartId, loadBuffer]);

  // ===========================================================================
  // Subscriptions
  // ===========================================================================

  useEffect(() => {
    if (DEBUG) devLog(`[TimeMotion:State] 🔧 Subscribing to events for chart ${chartId}`);

    const subscriptions: EventSubscription[] = [
      globalEventStream.subscribe('time.motion.open', handleOpen),
      globalEventStream.subscribe('time.motion.navigate', handleNavigate),
      globalEventStream.subscribe('time.motion.confirm', handleConfirm),
      globalEventStream.subscribe('time.motion.cancel', handleCancel),
      globalEventStream.subscribe('time.motion.settings.changed', handleSettingsChanged),
    ];

    return () => {
      subscriptions.forEach(sub => sub.unsubscribe());

      // Abort any in-progress load
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [handleOpen, handleNavigate, handleConfirm, handleCancel, handleSettingsChanged]);

  // Return nothing - this hook is purely event-driven
  return null;
}

export default useTimeMotionState;
